import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Share2, FileText, Download } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { api } from '../lib/api';
import { viewUrl, downloadUrl } from '../lib/files';
import { useToast } from '../components/Toast';
import { ShareSheet } from '../components/ShareSheet';
import { euro, dateIT, dateLongIT } from '../lib/format';

export function DettaglioGaranziaScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { data, loading } = useAsync(() => store.warranty(id), [id]);
  const { show } = useToast();
  const [share, setShare] = useState(false);

  if (loading) return <div className="screen">Caricamento…</div>;
  if (!data) return <div className="screen">Garanzia non trovata.</div>;

  const warranty = data;
  const firstAttachment = warranty.attachments[0];

  const onViewAttachment = async (attId: string) => {
    try {
      const { url } = await api.attachmentUrl(warranty.id, attId, 'inline');
      viewUrl(url);
    } catch {
      show('File non disponibile');
    }
  };

  const onDownloadReceipt = async () => {
    if (!firstAttachment) return show('Nessun allegato da scaricare');
    try {
      const { url } = await api.attachmentUrl(warranty.id, firstAttachment.id, 'attachment');
      downloadUrl(url, firstAttachment.filename);
    } catch {
      show('Download non disponibile');
    }
  };

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">{data.product}</div>
        <button className="iconbtn" aria-label="Condividi" onClick={() => setShare(true)}>
          <Share2 size={18} />
        </button>
      </div>

      {/* Hero countdown */}
      <div className="countdown">
        <div className="countdown__ring">
          {data.days_left < 0 ? '—' : `${data.days_left}`}
          <span style={{ fontSize: 9 }}>&nbsp;g</span>
        </div>
        <div>
          <div className="countdown__big">
            {data.status === 'expired' ? 'Garanzia scaduta' : `${data.days_left} giorni`}
          </div>
          <div className="countdown__sub">
            {data.status === 'expired' ? 'Scaduta il' : 'Garanzia in scadenza ·'} {dateLongIT(data.expires_at)}
          </div>
        </div>
      </div>

      {/* Righe dati */}
      <div className="card" style={{ padding: '4px 16px', marginTop: 18 }}>
        <Row label="Importo" value={euro(data.amount_cents)} />
        <Row label="Data acquisto" value={dateIT(data.purchased_at)} mono />
        <Row label="Venditore" value={data.seller ?? '—'} />
        {data.category && <Row label="Categoria" value={data.category} />}
        {data.notes && <Row label="Note" value={data.notes} />}
      </div>

      {/* Allegati */}
      {data.attachments.length > 0 && (
        <>
          <div className="section-head">
            <span className="eyebrow">Allegati</span>
          </div>
          <div className="list">
            {data.attachments.map((a) => (
              <button key={a.id} className="row" onClick={() => onViewAttachment(a.id)}>
                <span className="iconchip iconchip--sm">
                  <FileText size={18} />
                </span>
                <div className="row__body">
                  <div className="row__title" style={{ fontSize: 14 }}>
                    {a.filename}
                  </div>
                  <div className="row__meta">{a.size_bytes ? `${Math.round(a.size_bytes / 1024)} KB` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Action bar fissa */}
      <div className="actionbar">
        <button className="btn" style={{ flex: 1 }} onClick={() => setShare(true)}>
          <Share2 size={18} /> Condividi
        </button>
        <button className="btn btn--accent" style={{ flex: 2 }} onClick={onDownloadReceipt}>
          <Download size={18} /> Scarica scontrino
        </button>
      </div>

      {share && (
        <ShareSheet
          resourceType="warranty"
          resourceId={warranty.id}
          name={warranty.product}
          onClose={() => setShare(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="kv">
      <div>
        <div className="kv__label">{label}</div>
        <div className={`kv__value${mono ? ' kv__value--mono' : ''}`}>{value}</div>
      </div>
    </div>
  );
}
